import React, { FC, useContext, useEffect, useRef } from "react";
import cn from "clsx";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "@/redux/store";
import CircleRadio from "@/components/circleRadio";
import { selectCartData } from "@/util/selectors";
import { selectPDPProductInfo } from "../../selectors";
import { MobileSizesContext } from "../productMobileSizes";
import GlobalShippingIcon from "@/images/global_shipping_without_background.svg";
import { ProductSizeListProps } from "../types";

const ProductModalSizeList: FC<ProductSizeListProps> = ({
  productSizes,
  isActive
}) => {
  const { t } = useTranslation("productDetails");
  const { productId } = useAppSelector(selectPDPProductInfo);
  const cartItems = useAppSelector(selectCartData);

  const {
    onSizeSelection,
    productSizeExtraData,
    selectedSizeFullData: { size: selectedSize },
    openMultipleSizes
  } = useContext(MobileSizesContext);

  const listRef = useRef<HTMLUListElement>(null);
  const firstAvailableSizeRef = useRef<HTMLLIElement | null>(null);
  const hasSetFirstAvailableRef = useRef(false);

  useEffect(() => {
    if (openMultipleSizes && firstAvailableSizeRef.current) {
      firstAvailableSizeRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
    }
  }, [openMultipleSizes]);

  if (!productSizes?.length) return null;
  return (
    <ul className="size_list" ref={listRef} onClick={e => e.preventDefault()}>
      {productSizeExtraData}
      {productSizes?.map((size, index) => {
        if (!(size.sizeLabel || size.size)) return null;

        const isSizeChecked = isActive && selectedSize?.sizeId === size?.sizeId;
        const isOutOfStock = size.sizeStock <= 0;
        const isGlobalShipping = size.isGlobalShipping;
        const isSizePresentInCart = size => {
          const item = cartItems?.find?.(
            cartItem =>
              cartItem.id === productId && cartItem.sizeId === size.sizeId
          );
          return item;
        };

        let itemsLeftInfo = null;
        if (size.sizeStock <= 5 && size.sizeStock > 0) {
          itemsLeftInfo = <p>{t("peacesLeft", { count: size.sizeStock })}</p>;
        }

        return (
          <li
            key={index}
            onClick={() => {
              if (size.sizeStock) {
                //@ts-ignore
                onSizeSelection(size);
              }
            }}
            ref={el => {
              if (
                size.sizeStock > 0 &&
                !hasSetFirstAvailableRef.current &&
                el
              ) {
                firstAvailableSizeRef.current = el;
                hasSetFirstAvailableRef.current = true;
              }
            }}
            className={cn({
              size_box_color: isSizeChecked,
              "size_not_available not_clickable": isOutOfStock
            })}
          >
            <div className="circle-radio-wrapper">
              <CircleRadio isChecked={isSizeChecked} name={size.size} />
            </div>
            <span className="size_label">
              {(size.sizeLabel || size.size)?.trim()}
            </span>
            {isGlobalShipping && (
              <img src={GlobalShippingIcon} className="global_shipping_icon" />
            )}
            {isSizePresentInCart(size) ? (
              <p>{t("alreadyAddedToCart")}</p>
            ) : (
              itemsLeftInfo
            )}
          </li>
        );
      })}
    </ul>
  );
};

export default ProductModalSizeList;
